// /frontend/src/services/productService.js
import apiClient from '../utils/apiClient';

const API_URL = `${process.env.REACT_APP_API_URL}/products`;

export const productService = {
  // Get all products
  getProducts: async (params = {}) => {
    try {
      const response = await apiClient.get(API_URL, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get product by ID
  getProductById: async (id) => {
    try {
      const response = await apiClient.get(`${API_URL}/${id}`);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get products by category
  getProductsByCategory: async (categoryId) => {
    try {
      const response = await apiClient.get(`${API_URL}/category/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching products for category ${categoryId}:`, error);
      // If the category endpoint fails, try fetching all products and filtering client-side
      const allProducts = await apiClient.get(API_URL);
      const filteredProducts = allProducts.data.products.filter(
        product => product.category === categoryId
      );
      return { 
        ...allProducts.data, 
        products: filteredProducts 
      };
    }
  },
  
  // Create new product
  createProduct: async (productData) => {
    try {
      // Handle variant data for new product format
      if (productData.has('variantData')) {
        const variantDataStr = productData.get('variantData');
        const variantData = JSON.parse(variantDataStr);
        
        // Process variants to backend format
        processVariantData(productData, variantData);
      }
      
      const response = await apiClient.post(API_URL, productData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Update product
  updateProduct: async (id, productData) => {
    try {
      // Handle variant data for new product format
      if (productData.has('variantData')) {
        const variantDataStr = productData.get('variantData');
        const variantData = JSON.parse(variantDataStr);
        
        // Process variants to backend format
        processVariantData(productData, variantData);
      }
      
      const response = await apiClient.put(`${API_URL}/${id}`, productData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Delete product
  deleteProduct: async (id) => {
    try {
      const response = await apiClient.delete(`${API_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Update product stock
  updateProductStock: async (id, stockData) => {
    try {
      const response = await apiClient.patch(`${API_URL}/${id}/stock`, stockData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get product stock history
  getProductStockHistory: async (id) => {
    try {
      const response = await apiClient.get(`${API_URL}/${id}/stock-history`);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get categories
  getCategories: async () => {
    try {
      const response = await apiClient.get('/api/categories');
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },

  // Bulk import products
  bulkImportProducts: async (formData, onUploadProgress) => {
    try {
      // Get the token from localStorage or wherever you store it
      const token = localStorage.getItem('token');
      
      const response = await apiClient.post(`${API_URL}/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress
      });
      
      // Check if this is an async import response with an importId
      if (response.data && response.data.importId) {
        return response.data; // Return the full response for async imports
      }
      
      return response.data.data; // Return just the data for synchronous imports
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Check import status
  checkImportStatus: async (importId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await apiClient.get(`${API_URL}/import-status/${importId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Export products
  exportProducts: async () => {
    try {
      // Get the token from localStorage or wherever you store it
      const token = localStorage.getItem('token');
      
      const response = await apiClient.get(`${API_URL}/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // Create file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `products_export_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return true;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },

  // Search products
  searchProducts: async (query) => {
    try {
      // Initialize an empty array to collect all products
      let allProducts = [];
      let page = 1;
      let hasMorePages = true;
      const limit = 50; // Fetch 50 products per page to reduce number of requests
      
      // Fetch all pages of products
      while (hasMorePages) {
        const response = await apiClient.get(API_URL, {
          params: { page, limit }
        });
        
        if (!response.data || !response.data.products || response.data.products.length === 0) {
          break; // No more products to fetch
        }
        
        // Add products from this page to our collection
        allProducts = [...allProducts, ...response.data.products];
        
        // Check if there are more pages
        hasMorePages = response.data.pagination && response.data.pagination.next;
        page++;
        
        // Safety check to prevent infinite loops
        if (page > 100) {
          break;
        }
      }
      
      // Filter products based on the query
      const searchLower = query.toLowerCase();
      const filteredProducts = allProducts.filter(product => 
        (product.name && product.name.toLowerCase().includes(searchLower)) ||
        (product.description && product.description.toLowerCase().includes(searchLower)) ||
        (product.code && product.code.toLowerCase().includes(searchLower)) ||
        (product.category && typeof product.category === 'string' && product.category.toLowerCase().includes(searchLower))
      );
      
      // Limit to top 20 results for performance
      return filteredProducts.slice(0, 20);
    } catch (error) {
      console.error('Error searching products:', error);
      throw error.response?.data?.message || error.message;
    }
  },
};

// Helper function to process variant data for the backend
function processVariantData(formData, variantData) {
  // Remove the original variantData entry
  formData.delete('variantData');
  
  // Create backend format variantData array
  const backendVariantData = variantData.map((variant, index) => {
    // Generate a safe ID based on the variant name
    const variantId = variant.name.toLowerCase().replace(/\s+/g, '-');
    
    return {
      type: 'Color', // Default type, can be customized if needed
      name: variant.name,
      stock: variant.stock,
      // Include other variant-specific data if needed
      modelUrl: variant.modelUrl
    };
  });
  
  // Add processed variant data back to form data
  formData.append('variantData', JSON.stringify(backendVariantData));
}